import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";

import DistributionReviewApi from "../../../../../infrastructure/api/distributionReview";
import EnhancedTable from "../../../../components/shared/table";
import { canManageDistributionTeam } from "../../../../utils/authRoleHelpers";
import EDCBreadcrumbs, { EDC_MANAGEMENT_CRUMB, EDC_ROOT_CRUMB } from "../../EDCBreadcrumbs";
import EDCFormDialog from "../../EDCFormDialog";
import EDCListCardHeader from "../../EDCListCardHeader";
import { EDC_PATHS } from "../../edcPaths";
import useDistributionPortalContext, { pick } from "../../useDistributionPortalContext";
import AreaCitiesAutocomplete from "../../../SLN/shared/distribution/AreaCitiesAutocomplete";
import { listOf, normalizeLookup, useGridLookups } from "../grid/gridShared";

const INACTIVE_STATUS = 3;

const emptyArea = {
  id: null,
  name: "",
  code: "",
  engineeringUnitEnabled: false,
  isActive: true,
  cityIds: [],
  cities: [],
};

const accessLabel = (m) =>
  [
    pick(m, "canAreaReview", "CanAreaReview") ? "ناظر" : null,
    pick(m, "canEngineeringReview", "CanEngineeringReview") ? "مهندسی" : null,
  ]
    .filter(Boolean)
    .join(" / ");

const EDCAreasPage = () => {
  const { context, companyId, companyName, isCompanyAdmin, setSelectedCompanyId, reload, loading, error } =
    useDistributionPortalContext();
  const { lookups, reloadLookups } = useGridLookups(companyId, isCompanyAdmin);
  const [areas, setAreas] = useState([]);
  const [areasLoading, setAreasLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [engineeringFilter, setEngineeringFilter] = useState("");
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAreas = useCallback(async () => {
    if (!companyId || !isCompanyAdmin) {
      setAreas([]);
      return;
    }
    setAreasLoading(true);
    try {
      setAreas(listOf(await DistributionReviewApi.listAreas(companyId)));
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت امور");
    } finally {
      setAreasLoading(false);
    }
  }, [companyId, isCompanyAdmin]);

  useEffect(() => {
    loadAreas();
  }, [loadAreas]);

  const substationCountByArea = useMemo(() => {
    const map = new Map();
    lookups.substations.forEach((s) => {
      if (s.parentId != null) map.set(Number(s.parentId), (map.get(Number(s.parentId)) || 0) + 1);
    });
    return map;
  }, [lookups.substations]);

  const rows = useMemo(
    () =>
      areas
        .map((a) => {
          const id = Number(pick(a, "id", "Id"));
          const experts = (pick(a, "experts", "Experts") || []).filter(
            (m) => pick(m, "isActive", "IsActive") !== false
          );
          return {
            raw: a,
            id,
            name: pick(a, "name", "Name") || "—",
            code: pick(a, "code", "Code") || "",
            engineeringUnitEnabled: Boolean(pick(a, "engineeringUnitEnabled", "EngineeringUnitEnabled")),
            isActive: pick(a, "status", "Status") !== INACTIVE_STATUS,
            experts,
            expertCount: experts.length,
            cities: (pick(a, "cities", "Cities") || []).map(normalizeLookup),
            substationCount: substationCountByArea.get(id) || 0,
          };
        })
        .filter((r) => {
          if (statusFilter === "active" && !r.isActive) return false;
          if (statusFilter === "inactive" && r.isActive) return false;
          if (engineeringFilter === "on" && !r.engineeringUnitEnabled) return false;
          if (engineeringFilter === "off" && r.engineeringUnitEnabled) return false;
          return true;
        }),
    [areas, substationCountByArea, statusFilter, engineeringFilter]
  );

  const withoutExpert = useMemo(
    () => rows.filter((r) => r.isActive && r.expertCount === 0).length,
    [rows]
  );

  const withoutCities = useMemo(
    () => rows.filter((r) => r.isActive && r.cities.length === 0).length,
    [rows]
  );

  const headCells = [
    { id: "name", label: "نام امور" },
    { id: "code", label: "کد امور", render: (v) => v || "—" },
    {
      id: "cities",
      label: "شهرهای تحت پوشش",
      render: (v, row) =>
        row.cities.length === 0 ? (
          <Typography variant="body2" color="warning.main">
            ثبت نشده
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {row.cities.map((c) => (
              <Chip key={c.id} size="small" label={c.title} />
            ))}
          </Stack>
        ),
    },
    {
      id: "engineeringUnitEnabled",
      label: "واحد مهندسی",
      type: "boolean",
      model: { active: "فعال", deActive: "غیرفعال" },
    },
    {
      id: "expertCount",
      label: "کاربران و دسترسی",
      render: (v, row) =>
        row.experts.length === 0 ? (
          <Typography variant="body2" color="warning.main">
            بدون کاربر — پیامک ارجاع ارسال نمی‌شود
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {row.experts.map((m) => (
              <Chip
                key={pick(m, "id", "Id")}
                size="small"
                variant="outlined"
                label={`${pick(m, "userDisplayName", "UserDisplayName") || "—"}: ${accessLabel(m)}`}
              />
            ))}
          </Stack>
        ),
    },
    { id: "substationCount", label: "ایستگاه‌ها" },
    {
      id: "isActive",
      label: "وضعیت",
      type: "boolean",
      model: { active: "فعال", deActive: "غیرفعال" },
    },
  ];

  const openEdit = (row) =>
    setDialog({
      id: row.id,
      name: row.name === "—" ? "" : row.name,
      code: row.code,
      engineeringUnitEnabled: row.engineeringUnitEnabled,
      isActive: row.isActive,
      cityIds: row.cities.map((c) => c.id),
      cities: row.cities,
    });

  const setField = (field, value) => setDialog((s) => ({ ...s, [field]: value }));

  const save = async () => {
    if (!companyId || !dialog) return;
    if (!String(dialog.name || "").trim()) {
      toast.error("نام امور الزامی است");
      return;
    }
    setSaving(true);
    try {
      await DistributionReviewApi.saveArea({
        id: dialog.id || null,
        distributionCompanyId: Number(companyId),
        name: dialog.name.trim(),
        code: dialog.code?.trim() || null,
        engineeringUnitEnabled: Boolean(dialog.engineeringUnitEnabled),
        isActive: Boolean(dialog.isActive),
        cityIds: (dialog.cityIds || []).map(Number),
      });
      toast.success(dialog.id ? "تغییرات امور ذخیره شد" : "امور ایجاد شد");
      setDialog(null);
      await Promise.all([loadAreas(), reload()]);
    } catch (e) {
      toast.error(e?.message || "خطا در ذخیره امور");
    } finally {
      setSaving(false);
    }
  };

  const refresh = () => Promise.all([loadAreas(), reloadLookups()]);

  if (!canManageDistributionTeam()) return <Navigate to={EDC_PATHS.root} replace />;
  if (context && !isCompanyAdmin) return <Navigate to={EDC_PATHS.root} replace />;

  const busy = loading || areasLoading;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <EDCBreadcrumbs crumbs={[EDC_ROOT_CRUMB, EDC_MANAGEMENT_CRUMB, { title: "امور", to: EDC_PATHS.areas }]} />
      </Grid>

      {error ? (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <Card>
          <EDCListCardHeader
            title="امور شرکت توزیع"
            subheader={
              companyName
                ? `${companyName} — ${areas.length} امور`
                : "امورهای زیرمجموعه و وضعیت واحد مهندسی هر امور"
            }
            context={context}
            onCompanyChange={setSelectedCompanyId}
            onCreate={() => setDialog({ ...emptyArea })}
            createDisabled={!companyId}
            onRefresh={refresh}
            busy={busy}
          />
          <CardContent>
            {withoutExpert > 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {withoutExpert} امور فعال هنوز کاربری ندارد؛ پرونده‌های این امورها پیامک ارجاع ندارند. از
                صفحه «کاربران» کاربر را به امور بسپارید.
              </Alert>
            ) : null}
            {withoutCities > 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                برای {withoutCities} امور فعال شهرهای تحت پوشش ثبت نشده است؛ بدون آن، امور پرونده‌ها خودکار
                تشخیص داده نمی‌شود و باید هنگام ارجاع دستی انتخاب شود.
              </Alert>
            ) : null}

            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
              <TextField
                select
                size="small"
                label="واحد مهندسی"
                value={engineeringFilter}
                onChange={(e) => setEngineeringFilter(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">همه</MenuItem>
                <MenuItem value="on">فعال</MenuItem>
                <MenuItem value="off">غیرفعال</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="وضعیت"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">همه</MenuItem>
                <MenuItem value="active">فعال</MenuItem>
                <MenuItem value="inactive">غیرفعال</MenuItem>
              </TextField>
            </Box>

            <EnhancedTable
              rowKey="id"
              rows={rows}
              headCells={headCells}
              pageSize={100}
              loading={busy}
              defaultSort={{ orderBy: "name", order: "asc" }}
              actions={[
                {
                  icon: <EditIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "ویرایش",
                  onClick: openEdit,
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>

      <EDCFormDialog
        open={Boolean(dialog)}
        title={dialog?.id ? "ویرایش امور" : "ایجاد امور"}
        saving={saving}
        onClose={() => setDialog(null)}
        onSave={save}
        maxWidth="md"
      >
        <Grid item xs={12} md={7}>
          <TextField
            label="نام امور"
            value={dialog?.name || ""}
            onChange={(e) => setField("name", e.target.value)}
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            label="کد امور"
            value={dialog?.code || ""}
            onChange={(e) => setField("code", e.target.value)}
            fullWidth
            inputProps={{ dir: "ltr" }}
          />
        </Grid>
        <Grid item xs={12}>
          <AreaCitiesAutocomplete
            options={lookups.cities}
            selectedItems={dialog?.cities}
            value={dialog?.cityIds}
            onChange={(ids) => setField("cityIds", ids)}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(dialog?.engineeringUnitEnabled)}
                onChange={(e) => setField("engineeringUnitEnabled", e.target.checked)}
              />
            }
            label="واحد مهندسی این امور فعال باشد"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch checked={dialog?.isActive !== false} onChange={(e) => setField("isActive", e.target.checked)} />
            }
            label={dialog?.isActive !== false ? "امور فعال است" : "امور غیرفعال است"}
          />
        </Grid>
      </EDCFormDialog>
    </Grid>
  );
};

export default EDCAreasPage;
